import React, { useEffect, useState, useRef } from 'react';
import { PlayIcon, PauseIcon, Volume2Icon, VolumeXIcon } from 'lucide-react';
interface AudioPlayerProps {
  src: string;
  theme?: 'orange' | 'gold';
}
export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  src,
  theme = 'orange'
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  // ── Theme tokens ────────────────────────────────────────────────────────────
  const t = theme === 'gold' ? {
    playBtn: 'bg-[#D4AF37] hover:brightness-110',
    playBtnShadow: isPlaying ? 'shadow-[#D4AF37]/50' : 'shadow-[#D4AF37]/20',
    timeText: 'text-[#D4AF37]',
    trackBg: 'bg-[#0B1B3D]/80 border-[#D4AF37]/20',
    trackFill: 'bg-gradient-to-r from-[#D4AF37] to-[#f0d060] shadow-[0_0_8px_rgba(212,175,55,0.4)]',
    volBtnBg: 'bg-[#0B1B3D]/80 border-[#D4AF37]/20 hover:bg-[#D4AF37]/20 hover:border-[#D4AF37]/40',
    volIcon: 'text-[#D4AF37]',
    volFill: 'bg-gradient-to-r from-[#D4AF37] to-[#f0d060]',
    volTrackBg: 'bg-[#0B1B3D]/80 border-[#D4AF37]/20'
  } : {
    playBtn: 'bg-gradient-to-br from-orange-600 to-orange-500 hover:from-orange-500 hover:to-yellow-500',
    playBtnShadow: isPlaying ? 'shadow-orange-500/50' : 'shadow-orange-600/30',
    timeText: 'text-orange-300',
    trackBg: 'bg-gray-800/80 border-orange-900/30',
    trackFill: 'bg-gradient-to-r from-orange-600 via-orange-500 to-yellow-500 shadow-[0_0_8px_rgba(255,108,0,0.4)]',
    volBtnBg: 'bg-gray-800/80 border-orange-900/30 hover:bg-orange-900/40 hover:border-orange-700/50',
    volIcon: 'text-orange-400',
    volFill: 'bg-gradient-to-r from-orange-600 to-yellow-500',
    volTrackBg: 'bg-gray-800/80 border-orange-900/30'
  };
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newVolume = parseFloat(e.target.value);
    audio.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };
  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isMuted) {
      audio.volume = volume || 0.5;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };
  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  const progressPercentage = duration > 0 ? currentTime / duration * 100 : 0;
  return <div className="w-full" data-id="element-27">
      <audio ref={audioRef} src={src} preload="metadata" data-id="element-28" />
      <div className="flex items-center gap-4" data-id="element-29">
        {/* Play/Pause */}
        <button onClick={togglePlay} className={`flex-shrink-0 w-12 h-12 rounded-full ${t.playBtn} flex items-center justify-center transition-all duration-300 hover:scale-105 shadow-lg ${t.playBtnShadow} ${isPlaying ? 'animate-pulse-slow' : ''}`} aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-30">
          {isPlaying ? <PauseIcon className="h-6 w-6 text-white" data-id="element-31" /> : <PlayIcon className="h-6 w-6 text-white ml-1" data-id="element-32" />}
        </button>

        {/* Time */}
        <div className={`flex-shrink-0 text-sm font-mono ${t.timeText} font-medium min-w-[80px]`} data-id="element-33">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>

        {/* Progress */}
        <div className="flex-1 relative group" data-id="element-34">
          <div className={`h-2 ${t.trackBg} rounded-full overflow-hidden border`} data-id="element-35">
            <div className={`h-full ${t.trackFill} transition-all duration-150`} style={{
            width: `${progressPercentage}%`
          }} data-id="element-36" />
          </div>
          <input type="range" min="0" max={duration || 0} value={currentTime} onChange={handleSeek} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" aria-label="Seek audio" data-id="element-37" />
        </div>

        {/* Volume */}
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-38">
          <button onClick={toggleMute} className={`w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-300 ${t.volBtnBg}`} aria-label={isMuted ? 'Unmute' : 'Mute'} data-id="element-39">
            {isMuted ? <VolumeXIcon className={`h-4 w-4 ${t.volIcon}`} data-id="element-40" /> : <Volume2Icon className={`h-4 w-4 ${t.volIcon}`} data-id="element-41" />}
          </button>
          <div className="w-20 relative" data-id="element-42">
            <div className={`h-1.5 ${t.volTrackBg} rounded-full overflow-hidden border`} data-id="element-43">
              <div className={`h-full ${t.volFill} transition-all duration-150`} style={{
              width: `${(isMuted ? 0 : volume) * 100}%`
            }} data-id="element-44" />
            </div>
            <input type="range" min="0" max="1" step="0.01" value={isMuted ? 0 : volume} onChange={handleVolumeChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" aria-label="Volume" data-id="element-45" />
          </div>
        </div>
      </div>
    </div>;
};